import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';
import { LanguageCode } from '../../utils/constants';

const LocationsSection = ({ clinicId, onDataChange }) => {
  const [locations, setLocations] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddLocationForm, setShowAddLocationForm] = useState(false);
  const [newLocation, setNewLocation] = useState({
    primary_name: '',
    external_location_code: '',
    meta_data: {
      i18n: {
        name: {
          [LanguageCode.EN_US]: '',
          [LanguageCode.ZH_HK]: '',
          [LanguageCode.ZH_CN]: ''
        },
        address: {
          [LanguageCode.EN_US]: '',
          [LanguageCode.ZH_HK]: '',
          [LanguageCode.ZH_CN]: ''
        }
      }
    }
  });
  const [editingLocation, setEditingLocation] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Load locations when component mounts
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoading(true);
        const data = await clinicService.getClinicLocations(clinicId);
        setLocations(data.locations || []);
      } catch (err) {
        console.error('Error loading locations:', err);
        setError('Failed to load locations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, [clinicId]);

  // Load doctors so a location can REFERENCE a specific doctor's schedule as
  // its operating-hours source (or the union of all doctors when unset).
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const data = await clinicService.getClinicDoctors(clinicId);
        setDoctors(data?.doctors || []);
      } catch (err) {
        console.error('Error loading doctors:', err);
        setDoctors([]);
      }
    };

    loadDoctors();
  }, [clinicId]);

  // Build the non-i18n schedule-related meta_data (shared by add + update) so
  // phone/whatsapp/schedule and the doctor-schedule reference round-trip.
  const buildScheduleMetaData = (meta) => {
    const metaData = {};
    if (meta?.phone) metaData.phone = meta.phone;
    if (meta?.whatsapp) metaData.whatsapp = meta.whatsapp;
    const scheduleValue = (meta?.schedule || '').trim();
    if (scheduleValue) metaData.schedule = scheduleValue;
    if (meta?.use_doctor_schedule) {
      metaData.use_doctor_schedule = true;
      const docId = meta?.schedule_doctor_id;
      if (docId !== undefined && docId !== null && `${docId}`.trim() !== '') {
        metaData.schedule_doctor_id = parseInt(docId, 10);
      }
    }
    return metaData;
  };

  const handleNewLocationChange = (e) => {
    const { name, value } = e.target;
    setNewLocation(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNewLocationMetaChange = (field, value) => {
    setNewLocation(prev => ({
      ...prev,
      meta_data: {
        ...prev.meta_data,
        [field]: value
      }
    }));
  };

  const handleNewLocationI18nChange = (field, languageCode, value) => {
    setNewLocation(prev => ({
      ...prev,
      meta_data: {
        ...prev.meta_data,
        i18n: {
          ...prev.meta_data?.i18n,
          [field]: {
            ...prev.meta_data?.i18n?.[field],
            [languageCode]: value
          }
        }
      }
    }));
  };

  const handleAddLocation = async () => {
    // Validate required fields
    if (!newLocation.primary_name) {
      setError('Please fill in the primary name.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Build meta_data if any i18n names or addresses are provided
      const hasI18nNames = newLocation.meta_data?.i18n?.name &&
        Object.values(newLocation.meta_data.i18n.name).some(name => name && name.trim());
      const hasI18nAddresses = newLocation.meta_data?.i18n?.address &&
        Object.values(newLocation.meta_data.i18n.address).some(addr => addr && addr.trim());

      const locationData = {
        primary_name: newLocation.primary_name,
        external_location_code: newLocation.external_location_code || null
      };

      // Preserve non-i18n meta_data keys (phone/whatsapp) and add schedule.
      const metaData = buildScheduleMetaData(newLocation.meta_data);

      if (hasI18nNames || hasI18nAddresses) {
        metaData.i18n = {};
        if (hasI18nNames) {
          metaData.i18n.name = Object.fromEntries(
            Object.entries(newLocation.meta_data.i18n.name).filter(([_, value]) => value && value.trim())
          );
        }
        if (hasI18nAddresses) {
          metaData.i18n.address = Object.fromEntries(
            Object.entries(newLocation.meta_data.i18n.address).filter(([_, value]) => value && value.trim())
          );
        }
      }

      if (Object.keys(metaData).length > 0) {
        locationData.meta_data = metaData;
      }

      const data = await clinicService.createClinicLocation(clinicId, locationData);
      setLocations(prev => [...prev, data.location]);
      setNewLocation({
        primary_name: '',
        external_location_code: '',
        meta_data: {
          i18n: {
            name: {
              [LanguageCode.EN_US]: '',
              [LanguageCode.ZH_HK]: '',
              [LanguageCode.ZH_CN]: ''
            },
            address: {
              [LanguageCode.EN_US]: '',
              [LanguageCode.ZH_HK]: '',
              [LanguageCode.ZH_CN]: ''
            }
          }
        }
      });
      setShowAddLocationForm(false);
      setSuccessMessage('Location added successfully');
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding location:', err);
      setError(err.response?.data?.detail || 'Failed to add location. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setNewLocation({
      primary_name: location.primary_name || '',
      external_location_code: location.external_location_code || '',
      meta_data: location.meta_data || {
        i18n: {
          name: {
            [LanguageCode.EN_US]: '',
            [LanguageCode.ZH_HK]: '',
            [LanguageCode.ZH_CN]: ''
          },
          address: {
            [LanguageCode.EN_US]: '',
            [LanguageCode.ZH_HK]: '',
            [LanguageCode.ZH_CN]: ''
          }
        }
      }
    });
    setShowAddLocationForm(true);
  };

  const handleUpdateLocation = async () => {
    if (!editingLocation) return;

    // Validate required fields
    if (!newLocation.primary_name) {
      setError('Please fill in the primary name.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // Build meta_data if any i18n names or addresses are provided
      const hasI18nNames = newLocation.meta_data?.i18n?.name &&
        Object.values(newLocation.meta_data.i18n.name).some(name => name && name.trim());
      const hasI18nAddresses = newLocation.meta_data?.i18n?.address &&
        Object.values(newLocation.meta_data.i18n.address).some(addr => addr && addr.trim());

      const locationData = {
        primary_name: newLocation.primary_name,
        external_location_code: newLocation.external_location_code || null
      };

      // Preserve non-i18n meta_data keys (phone/whatsapp) and add schedule.
      const metaData = buildScheduleMetaData(newLocation.meta_data);

      if (hasI18nNames || hasI18nAddresses) {
        metaData.i18n = {};
        if (hasI18nNames) {
          metaData.i18n.name = Object.fromEntries(
            Object.entries(newLocation.meta_data.i18n.name).filter(([_, value]) => value && value.trim())
          );
        }
        if (hasI18nAddresses) {
          metaData.i18n.address = Object.fromEntries(
            Object.entries(newLocation.meta_data.i18n.address).filter(([_, value]) => value && value.trim())
          );
        }
      }

      if (Object.keys(metaData).length > 0) {
        locationData.meta_data = metaData;
      }

      const data = await clinicService.updateClinicLocation(clinicId, editingLocation.id, locationData);
      setLocations(prev => prev.map(loc => loc.id === editingLocation.id ? data.location : loc));
      setEditingLocation(null);
      setNewLocation({
        primary_name: '',
        external_location_code: '',
        meta_data: {
          i18n: {
            name: {
              [LanguageCode.EN_US]: '',
              [LanguageCode.ZH_HK]: '',
              [LanguageCode.ZH_CN]: ''
            },
            address: {
              [LanguageCode.EN_US]: '',
              [LanguageCode.ZH_HK]: '',
              [LanguageCode.ZH_CN]: ''
            }
          }
        }
      });
      setShowAddLocationForm(false);
      setSuccessMessage('Location updated successfully');
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating location:', err);
      setError(err.response?.data?.detail || 'Failed to update location. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) {
      return;
    }

    try {
      setLoading(true);
      await clinicService.deleteClinicLocation(clinicId, locationId);
      setLocations(prev => prev.filter(loc => loc.id !== locationId));
      setSuccessMessage('Location deleted successfully');
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting location:', err);
      setError(err.response?.data?.detail || 'Failed to delete location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowAddLocationForm(false);
    setEditingLocation(null);
    setNewLocation({
      primary_name: '',
      external_location_code: '',
      meta_data: {
        i18n: {
          name: {
            [LanguageCode.EN_US]: '',
            [LanguageCode.ZH_HK]: '',
            [LanguageCode.ZH_CN]: ''
          },
          address: {
            [LanguageCode.EN_US]: '',
            [LanguageCode.ZH_HK]: '',
            [LanguageCode.ZH_CN]: ''
          }
        }
      }
    });
    setError(null);
  };

  const getLanguageLabel = (code) => {
    const labels = {
      [LanguageCode.EN_US]: 'English',
      [LanguageCode.ZH_HK]: '繁體中文',
      [LanguageCode.ZH_CN]: '简体中文'
    };
    return labels[code] || code;
  };

  if (loading && locations.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <p className="text-gray-500">Loading locations...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-700">Clinic Locations</h3>
        {!showAddLocationForm && (
          <button
            onClick={() => setShowAddLocationForm(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Location
          </button>
        )}
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {showAddLocationForm && (
        <div className="mb-6 p-6 border rounded-lg bg-gray-50">
          <h4 className="text-lg font-medium mb-4">
            {editingLocation ? 'Edit Location' : 'Add New Location'}
          </h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="primary_name"
                value={newLocation.primary_name}
                onChange={handleNewLocationChange}
                className="w-full p-2 border rounded"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                External Location Code
              </label>
              <input
                type="text"
                name="external_location_code"
                value={newLocation.external_location_code}
                onChange={handleNewLocationChange}
                className="w-full p-2 border rounded"
                placeholder="Optional external code"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Operating Hours (Schedule)
              </label>
              <textarea
                value={newLocation.meta_data?.schedule || ''}
                onChange={(e) => handleNewLocationMetaChange('schedule', e.target.value)}
                className="w-full p-2 border rounded"
                rows="2"
                placeholder="e.g. Mon-Fri 10:00-20:30; Sat-Sun 10:00-17:30; closed on public holidays"
              />
              <p className="text-xs text-gray-500 mt-1">
                Shown to the AI for this branch. Used as the booking time guard so the assistant won't confirm times outside these hours.
              </p>
            </div>

            <div className="border-t pt-4">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  className="mr-2"
                  checked={!!newLocation.meta_data?.use_doctor_schedule}
                  onChange={(e) => handleNewLocationMetaChange('use_doctor_schedule', e.target.checked)}
                />
                Use clinic doctor schedule for operating hours
              </label>
              <p className="text-xs text-gray-500 mt-1">
                When enabled, this branch's hours are taken live from the structured doctor schedule (date- and public-holiday-aware) instead of the free text above. The free text is kept only as a fallback/display.
              </p>
              {newLocation.meta_data?.use_doctor_schedule && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Doctor (optional)
                  </label>
                  <select
                    value={newLocation.meta_data?.schedule_doctor_id ?? ''}
                    onChange={(e) => handleNewLocationMetaChange('schedule_doctor_id', e.target.value === '' ? null : e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">All doctors (union of schedules)</option>
                    {doctors.map((doctor) => (
                      <option key={doctor.id} value={doctor.id}>
                        {doctor.primary_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Leave as "All doctors" to use the combined opening hours of every doctor at this clinic.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <h5 className="text-sm font-medium text-gray-700 mb-3">Internationalized Names (Optional)</h5>
              {Object.entries(LanguageCode).map(([key, code]) => (
                <div key={code} className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name ({getLanguageLabel(code)})
                  </label>
                  <input
                    type="text"
                    value={newLocation.meta_data?.i18n?.name?.[code] || ''}
                    onChange={(e) => handleNewLocationI18nChange('name', code, e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder={`Name in ${getLanguageLabel(code)}`}
                  />
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <h5 className="text-sm font-medium text-gray-700 mb-3">Internationalized Addresses (Optional)</h5>
              {Object.entries(LanguageCode).map(([key, code]) => (
                <div key={code} className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address ({getLanguageLabel(code)})
                  </label>
                  <textarea
                    value={newLocation.meta_data?.i18n?.address?.[code] || ''}
                    onChange={(e) => handleNewLocationI18nChange('address', code, e.target.value)}
                    className="w-full p-2 border rounded"
                    rows="2"
                    placeholder={`Address in ${getLanguageLabel(code)}`}
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={editingLocation ? handleUpdateLocation : handleAddLocation}
                disabled={isSaving}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : (editingLocation ? 'Update' : 'Add')}
              </button>
            </div>
          </div>
        </div>
      )}

      {!showAddLocationForm && (
        <div>
          {locations.length === 0 ? (
            <p className="text-gray-500">No locations found. Add your first location to get started.</p>
          ) : (
            <div className="space-y-4">
              {locations.map(location => (
                <div key={location.id} className="border rounded-lg p-4 bg-white">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h5 className="font-medium text-gray-700">{location.primary_name}
                      {location.external_location_code && (
                        <span className="text-xs text-gray-500 ml-1">({location.external_location_code})</span>
                      )}
                      </h5>
                      {location.meta_data?.i18n?.name && Object.keys(location.meta_data.i18n.name).length > 0 && (
                        <div className="mt-2">
                          {Object.entries(location.meta_data.i18n.name).map(([lang, name]) => (
                            <div key={lang} className="text-xs text-gray-600 mr-2">
                              <span className="font-medium">{getLanguageLabel(lang)}:</span> {name}
                            </div>
                          ))}
                        </div>
                      )}
                      {location.meta_data?.i18n?.address && Object.keys(location.meta_data.i18n.address).length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-500">Localized Addresses:</p>
                          {Object.entries(location.meta_data.i18n.address).map(([lang, address]) => (
                            <div key={lang} className="text-xs text-gray-600">
                              {getLanguageLabel(lang)}: {address}
                            </div>
                          ))}
                        </div>
                      )}
                      {location.meta_data?.schedule && (
                        <div className="mt-2 text-xs text-gray-600">
                          <span className="font-medium">Hours:</span> {location.meta_data.schedule}
                        </div>
                      )}
                      {location.meta_data?.use_doctor_schedule && (
                        <div className="mt-1 text-xs text-blue-600">
                          <span className="font-medium">Hours source:</span> Doctor schedule
                          {location.meta_data?.schedule_doctor_id
                            ? ` (${doctors.find(d => d.id === location.meta_data.schedule_doctor_id)?.primary_name || `Doctor #${location.meta_data.schedule_doctor_id}`})`
                            : ' (all doctors)'}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditLocation(location)}
                        className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteLocation(location.id)}
                        className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationsSection;

